<template>
	<div v-if="dataFiles.length === 0">
		<v-alert type="error">You have to select a folder with at least one TXT or CSV file with subject data in it</v-alert>
		<v-btn color="primary" raised x-large @click="startOver">Start Over</v-btn>
	</div>
	<div v-else-if="imageFiles.length === 0 && matchingSubjectCount === 0">
		<v-alert type="error">You have to select a folder with at least one subject image in it</v-alert>
		<v-btn color="primary" raised x-large @click="startOver">Start Over</v-btn>
	</div>
	<div v-else>
		<v-select :items="dataFiles" :item-text="dataFileDisplay" :item-value="dataFileValue" v-model="selectedDataFile" attach label="Data File" />
		<v-checkbox :input-value="selectFirstRowHeader" @click="toggleFirstRowHeader" label="First Row Header" class="first-row-header" v-if="!readmeRowHeader && subjectData?.length > 0" />
		<v-checkbox :input-value="useCropDataFile" @click="useCropDataFile = !useCropDataFile" :label="'Upload crops from ' + cropDataFile.file.name" class="first-row-header no-margin-top use-crop-data-checkbox" v-if="cropDataFile" />

		<v-sheet elevation="2" class="merge-settings-wrapper text-start" v-if="subjectData?.length > 0">
			<h4 style="text-align: left">Merge Settings</h4>
			A subject will be merged if each of these selected fields match an existing subject.<br/>
			If left blank, there will only be a merge if <i>all mapped fields</i> match an existing subject.<br/>
			If you set one merge field, and it is blank, that row will be merged matching all fields so that merging new subjects without a Student ID won't treat them all as the same subject.
			<v-select :items="fieldItems" v-model="mergeFields" attach multiple label="Merge Fields" chips deletable-chips class="merge-fields" />
		</v-sheet>

		<v-sheet v-if="subjectData && !loading" elevation="2" class="subject-grid-wrapper text-start">
			<v-alert type="error" v-if="subjectData.length === 0">Index file is blank.  Please choose another data file.</v-alert>
			<subject-mapping-table
				:items="uniqueSubjectRows"
				:subject-field-headers="subjectFieldHeaders"
				:field-items="fieldItems"
				:default-row-header="defaultRowHeader"
				:image-map="imageMap"
				:alt-image-map="altImageMap"
				:image-file-column="imageFileColumn"
				:editable="true"
				:items-per-page="10"
				:get-matching-rows="getMatchingRows"
				:project="project"
				:matching-subjects="matchingUniqueSubjects"
				v-else />
		</v-sheet>

		<v-progress-circular v-if="loading" indeterminate />
		<div v-else-if="subjectMatchingError">
			<v-alert type="error">{{ subjectMatchingError }}</v-alert>
			<v-btn color="primary" raised x-large @click="startOver">Start Over</v-btn>
		</div>
		<template v-else>
			<v-expansion-panels class="warnings" v-if="subjectsMissingImagesAndMatch.length > 0">
				<v-expansion-panel>
					<v-expansion-panel-header>
						<v-alert type="warning">
							There are {{ subjectsMissingImagesAndMatch.length }} subjects missing an image.  Please check to make sure that the filename matches exactly what is in the data file.
						</v-alert>
					</v-expansion-panel-header>

					<v-expansion-panel-content>
						<subject-mapping-table
							:items="subjectsMissingImagesAndMatch"
							:subject-field-headers="subjectFieldHeaders"
							:field-items="fieldItems"
							:default-row-header="defaultRowHeader"
							:image-map="imageMap"
							:alt-image-map="altImageMap"
							:image-file-column="imageFileColumn"
							:editable="false"
							:items-per-page="5"
							class="text-start"
							:project="project" />
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
			<v-alert type="warning" v-if="uniqueSubjectRows.length === 1 && matchedImagesCount > 1 && mergeFields.length">Are you sure you want to proceed with all images being uploaded to a single subject?  Please double check your merge field settings above.</v-alert>

			<v-alert type="success" v-if="subjectData?.length > 0">Clicking start will upload {{ matchedImagesCount }} images to {{ uniqueSubjectRows.length }} subjects</v-alert>
			<v-alert type="warning" v-if="!hasFieldsMapped && matchedImagesCount > 0">Please map at least 2 fields in the table above in order to start uploading</v-alert>

			<template v-if="rejectedUploads.length">
				<p/>
				<rejected-files-table :rejected-uploads="rejectedUploads" />
			</template>

			<v-btn class="back final-button" raised @click="startOver">Back</v-btn>
			<v-btn class="final-button" color="primary" raised x-large @click="startUpload" :disabled="!canStartUpload">Start Uploading</v-btn>
		</template>
	</div>
</template>

<script>
import Vue from 'vue';
import SubjectMappingTable from './SubjectMappingTable';
import { client, plicAxios, shouldRetryAll } from '../utils/axios';
import { logError } from '../utils/bugsnag';
import Papa from 'papaparse';
import checkIsValidImage from './utils/is-valid-image';
import createUpload from './utils/create-upload';
import simpleFilename from './utils/simple-filename';
import simpleAltFilename from './utils/simple-alt-name';
import isDataFile from './utils/is-data-file';
import RejectedFilesTable from './RejectedFilesTable.vue';
import moment from 'moment';

function getDefaultState() {
	return {
		dataFileLoading: false,
		dataFiles: [],
		selectedDataFile: null,
		subjectMatchingError: null,
		subjectData: null,
		subjectFieldHeaders: null,
		firstRowHeader: false,
		imageMap: {},
		altImageMap: {},
		imageFileColumn: 0,
		matchedImagesCount: 0,
		readmeRowHeader: null,
		mergeFields: [],
		useCropDataFile: false,
		cropData: null,
		cropDataFile: null,
		cropDataFileLoading: false
	};
}

export default {
	name: 'SubjectMapping',
	props: {
		uploads: null,
		imageRequirements: null,
		project: null,
		projectImportId: null,
		isVisible: null
	},
	data() {
		return getDefaultState();
	},
	watch: {
		isVisible() {
			if(!this.isVisible) {
				return;
			}

			let replaceState = getDefaultState();
			for(let prop in replaceState) {
				this[prop] = replaceState[prop];
			}

			this.dataFiles = this.uploads.filter((upload) => {
				return isDataFile(upload.file);
			});
			this.selectedDataFile = this.dataFiles.filter((upload) => {
				return upload.file.name.toLowerCase() === 'index.txt';
			})[0] ?? this.dataFiles[0];

			this.imageFiles = this.uploads.filter((upload) => {
				return this.imageRequirements.validTypes.indexOf(upload.file.type.toLowerCase()) !== -1;
			});
			this.imageFiles.forEach((upload) => {
				this.checkIsValidImage(upload);

				if(!upload.rejectReason) {
					this.imageMap[simpleFilename(upload.file.name)] = upload;
					this.altImageMap[simpleFilename(upload.file.name)] = upload;
				}
			});
			this.lookForCropDataFile();

			this.uploads.forEach((upload) => {
				if(!this.imageFiles.includes(upload)) {
					upload.skipUpload = true;
				}
			});
		},
		selectedDataFile() {
			if(!this.selectedDataFile) {
				return;
			}

			this.parseSelectedDataFile(this.selectedDataFile);
		},
		subjectData() {
			if(!this.subjectData || !this.subjectData.length) {
				return;
			}

			let maxColumns = this.subjectData.reduce((maxLength, row) => {
				return Math.max(maxLength, row.length);
			}, 1);
			this.subjectFieldHeaders = [];
			let imagesInColumns = [];
			for(let i = 0; i < maxColumns; i++) {
				this.subjectFieldHeaders.push('');
				imagesInColumns.push(0);
			}

			// Try to detect whether the first row is the header
			let matchingHeaders = this.subjectData[0].filter((column) => {
				return this.project.templateFields.filter((field) => {
					return field.effective_label == column || field.label == column || field.field_key == column;
				}).length > 0;
			});
			if(matchingHeaders.length >= 2) {
				this.firstRowHeader = this.subjectData[0];

				this.firstRowHeader.forEach((column, index) => {
					Vue.set(this.subjectFieldHeaders, index, this.project.templateFields.filter((field) => {
						return field.effective_label == column || field.label == column || field.field_key == column;
					})[0] ?? null);
				});
			} else {
				this.lookForReadMe();
			}

			// Try to detect which column is the image name
			this.subjectData.forEach((row) => {
				row.forEach((column, columnIndex) => {
					if(this.imageMap[simpleFilename(column)]) {
						imagesInColumns[columnIndex]++;
					} else if(this.altImageMap[simpleAltFilename(column)]) {
						imagesInColumns[columnIndex]++;
					}
				});
			});

			let maxMatchedImages = 0;
			let maxMatchedImagesIndex = 0;
			imagesInColumns.forEach((matchedImages, index) => {
				if(matchedImages > maxMatchedImages) {
					maxMatchedImagesIndex = index;
					maxMatchedImages = matchedImages;
				}
			});
			if(maxMatchedImages === 0 && this.firstRowHeader) {
				// This helps with being able to tell if we have an empty value for image name in data-only merges
				this.imageFileColumn = this.firstRowHeader.findIndex(header => ['image file', 'image name'].includes(header.toLowerCase())) ?? 0;
			} else {
				this.imageFileColumn = maxMatchedImagesIndex;
			}
			this.matchedImagesCount = maxMatchedImages;
		},
		useCropDataFile() {
			this.updateCropData();
		}
	},
	computed: {
		fieldItems() {
			let items = this.project.templateFields.map((field) => {
				return {
					text: field.effective_label,
					value: field
				};
			});

			items.sort((a, b) => {
				var aVal = a.text.toLowerCase();
				var bVal = b.text.toLowerCase();

				if(!aVal && !bVal) {
					return 0;
				} else if(!aVal) {
					return -1;
				} else if(!bVal) {
					return 1;
				} else {
					if(aVal == bVal) {
						return 0;
					} else {
						return aVal < bVal ? -1 : 1;
					}
				}
			});

			return items;
		},
		canStartUpload() {
			// We always need to have fields mapped
			// We need either some images matched to a subject OR we have some matching subjects with images already we are uploading changes for
			return this.hasFieldsMapped && (this.matchedImagesCount > 0 || this.matchingSubjects.filter(subject => subject?.photos?.length > 0).length  > 0);
		},
		hasFieldsMapped() {
			return this.subjectFieldHeaders && this.subjectFieldHeaders.filter((header) => {
				return !!header;
			}).length >= 2;
		},
		subjectDataVisible() {
			if(this.firstRowHeader) {
				return this.subjectData.slice(1);
			} else {
				return this.subjectData ?? [];
			}
		},
		subjectDataVisibleStrings() {
			return this.subjectDataVisible.map((row) => {
				return this.getSubjectRowMatchString(row, true);
			});
		},
		uniqueSubjectRows() {
			return this.subjectDataVisible.filter((row) => {
				let matchingRows = this.getMatchingRows(row);
				return !matchingRows.length || matchingRows[0] == row;
			});
		},
		subjectsMissingImages() {
			return this.subjectDataVisible.filter(row => {
				let imageName = simpleFilename(row[this.imageFileColumn]);
				return !this.imageMap[imageName] && !this.altImageMap[simpleAltFilename(row[this.imageFileColumn])];
			});
		},
		subjectsMissingImagesAndMatch() {
			return this.subjectDataVisible.filter((row, rowIndex) => {
				let imageName = simpleFilename(row[this.imageFileColumn]);
				// In case we are merging data changes to an existing subject, we still want to show an error if we are referencing a image which is missing from upload
				return !this.imageMap[imageName]  && !this.altImageMap[simpleAltFilename(row[this.imageFileColumn])] && (!this.matchingSubjects[rowIndex] || imageName);
			});
		},
		rejectedUploads() {
			return (this.uploads || []).filter((upload) => {
				return !!upload.rejectReason;
			});
		},
		selectFirstRowHeader() {
			return !!this.firstRowHeader;
		},
		defaultRowHeader() {
			return this.firstRowHeader || this.readmeRowHeader;
		},
		loading() {
			return this.dataFileLoading || this.cropDataFileLoading;
		},
		isGroupImageFieldIndex() {
			if(this.firstRowHeader && this.subjectData.length) {
				for(let i = 0; i < this.subjectData[0].length; i++) {
					let field = this.subjectData[0][i];
					// Exclude Is Group Image only if not mapped to a field
					if(field === 'Is Group Image' && !this.subjectFieldHeaders[i]) {
						return i;
					}
				}
			}

			return -1;
		},
		matchingSubjects() {
			if(this.subjectDataVisible) {
				return this.getMatchingSubjects(this.subjectDataVisible);
			} else {
				return [];
			}
		},
		matchingSubjectCount() {
			return this.matchingSubjects.filter(subject => subject?.photos?.length > 0).length;
		},
		matchingUniqueSubjects() {
			return this.getMatchingSubjects(this.uniqueSubjectRows);
		}
	},
	methods: {
		checkIsValidImage,
		startUpload() {
			this.setupUploadsWithSubjectData();
			this.$emit('next-step');
		},
		setupUploadsWithSubjectData() {
			this.imageFiles.forEach((upload) => {
				this.setupUploadWithSubjectData(upload);
			});

			this.subjectsMissingImages.forEach((row) => {
				let upload = createUpload({
					startUpload: (uploadFileRow) => {
						this.createSubjects(upload, [row], () => {
							uploadFileRow.finishUploadComplete(upload);
						}, (errorMessage, error) => {
							uploadFileRow.failedUpload(upload, errorMessage, error, true);
						});
					},
					subjects: [],
					description: 'Missing Image - ' + this.getSubjectNameForRow(row)
				});
				this.uploads.push(upload);
			});
		},
		setupUploadWithSubjectData(upload) {
			let subjectRows = this.subjectData.filter((row) => {
				let imageName = simpleFilename(row[this.imageFileColumn]);
				return this.imageMap[imageName] == upload || this.altImageMap[simpleAltFilename(row[this.imageFileColumn])] == upload;
			});

			if(subjectRows.length) {
				upload.description = upload.file.name + ' - ' + this.getSubjectNameForRow(subjectRows[0]);
				if(this.isGroupImageIndex !== -1 && subjectRows[0][this.isGroupImageFieldIndex] === 'Yes') {
					upload.photoCategory = 'group_category';
				}
				Vue.set(upload, 'subjects', []);

				upload.onPhotoUploaded = (resolve, reject) => {
					upload.progress = 90;
					upload.progressInfo = 'Creating subject';
					this.createSubjects(upload, subjectRows, resolve, reject);
				};
				upload.maxDefaultPercentage = 90;
			}
		},
		getSubjectNameForRow: function(row) {
			let firstNameColumn = this.getColumnForField('First Name');
			let lastNameColumn = this.getColumnForField('Last Name');
			if(firstNameColumn && lastNameColumn && row[firstNameColumn] && row[lastNameColumn]) {
				return row[firstNameColumn] + ' ' + row[lastNameColumn];
			} else {
				return '';
			}
		},
		getColumnForField(field) {
			for(let i = 0; i < this.subjectFieldHeaders.length; i++) {
				let subjectFieldHeader = this.subjectFieldHeaders[i];
				if(subjectFieldHeader && (subjectFieldHeader.effective_label == field || subjectFieldHeader.label == field || subjectFieldHeader.field_key == field)) {
					return i;
				}
			}

			return false;
		},
		createSubjects(upload, subjectRows, resolve, reject) {
			let properties = {};
			let subjectRow = subjectRows[0];
			subjectRow.forEach((column, columnIndex) => {
				let field = this.subjectFieldHeaders[columnIndex];
				if(field) {
					properties[field.id] = column;
				}
			});

			let isPrimaryPhoto = true;
			let matchingRows = this.getMatchingRows(subjectRow).filter((subjectRow) => {
				return this.isGroupImageIndex === -1 || subjectRow[this.isGroupImageFieldIndex] !== 'Yes';
			});
			if(matchingRows.length && matchingRows[matchingRows.length - 1] != subjectRow) {
				isPrimaryPhoto = false;
			} else if(this.isGroupImageIndex !== -1 && subjectRow[this.isGroupImageFieldIndex] === 'Yes') {
				isPrimaryPhoto = false;
			}

			let params = {
				subject: {
					properties,
					created_by_plic_app_id: process.env.VUE_APP_PLIC_APP_SLUG
				},
				photo_id: upload?.photo?.id,
				photo_is_primary: isPrimaryPhoto,
				merge_duplicate: true,
				project_import_id: this.projectImportId
			};

			let mergeFields = this.mergeFields.map((field) => {
				return field.id;
			}).filter((field) => !field || !field.id);
			if(mergeFields.length && this.getSubjectRowMatchString(subjectRow) !== '') {
				params.merge_field_ids = mergeFields;
			}

			if(upload.file) {
				upload.description = upload.file.name + ' - ' + this.getSubjectNameForRow(subjectRow);
			} else {
				upload.description = 'Missing Image - ' + this.getSubjectNameForRow(subjectRow);
			}
			plicAxios.post('projects/' + this.project.plicProjectId + '/subjects', params, {
				raxConfig: {
					shouldRetry: shouldRetryAll
				}
			}).then(async (response) => {
				let subject = response.data.subject;
				upload.subjects.push(subject);

				if(!!subject.primary_photo_id && subject.created_at && subject.updated_at && Math.abs(moment(subject.created_at).diff(subject.updated_at)) <= 2000) {
					upload.progress = 95;
					upload.progressInfo = 'Creating subject - adding to batch';
					try {
						await client.post('ajax/onSubjectCreated.php', {
							jobId: this.project.jobId,
							subject,
							subjectFields: this.project.templateFields
						}, {
							timeout: 30_000,
							raxConfig: {
								shouldRetry: shouldRetryAll
							}
						});
					} catch(e) {
						console.warn('Failed to add subject to batch: ', e);
					}
				}

				let remainingRows = subjectRows.slice(1);
				if(remainingRows.length) {
					this.createSubjects(upload, remainingRows, resolve, reject);
				} else {
					resolve();
				}
			}).catch((error) => {
				reject('Failed to create subject', error);
			});
		},
		getMatchingRows(subjectRow, includeSelf = true) {
			let rows = [];

			let subjectRowStr = this.getSubjectRowMatchString(subjectRow, true);
			this.subjectDataVisibleStrings.forEach((rowStr, index) => {
				if(rowStr == subjectRowStr && subjectRowStr !== '') {
					let row = this.subjectDataVisible[index];
					if(includeSelf || row != subjectRow) {
						rows.push(row);
					}
				}
			});

			return rows;
		},
		getSubjectRowMatchString(row, fallbackToAllOnBlank = false) {
			let subjectRowCopy = [...row];

			if(this.mergeFields.length) {
				let mergeIndexes = this.mergeFields.map((field) => {
					for(let i = 0; i < this.subjectFieldHeaders.length; i++) {
						let subjectFieldHeader = this.subjectFieldHeaders[i];
						if(subjectFieldHeader && subjectFieldHeader.id == field.id) {
							return i;
						}
					}

					return null;
				}).filter(index => !!index);

				if(mergeIndexes.length) {
					let subjectFieldCopy = mergeIndexes.map((index) => {
						if(index) {
							return subjectRowCopy[index];
						} else {
							return '';
						}
					});

					if(subjectFieldCopy.length === 1 && subjectFieldCopy[0] === '' && fallbackToAllOnBlank) {
						subjectRowCopy = this.getSubjectRowMatchStringDefault(subjectRowCopy);
					} else {
						subjectRowCopy = subjectFieldCopy;
					}
				} else {
					subjectRowCopy = this.getSubjectRowMatchStringDefault(subjectRowCopy);
				}
			} else {
				subjectRowCopy = this.getSubjectRowMatchStringDefault(subjectRowCopy);
			}

			return subjectRowCopy.join(', ');
		},
		getSubjectRowMatchStringDefault(subjectRowCopy) {
			let indexesToRemove = [this.imageFileColumn];

			if(this.isGroupImageFieldIndex !== -1) {
				indexesToRemove.push(this.isGroupImageFieldIndex);
			}

			indexesToRemove.sort();
			for(let i = indexesToRemove.length - 1; i >= 0; i--) {
				subjectRowCopy.splice(indexesToRemove[i], 1);
			}

			return subjectRowCopy;
		},
		startOver() {
			this.$emit('prev-step');
		},
		dataFileDisplay(upload) {
			return upload.file.webkitRelativePath || upload.file.name;
		},
		dataFileValue(upload) {
			return upload;
		},
		toggleFirstRowHeader() {
			if(this.firstRowHeader) {
				this.firstRowHeader = false;
				this.subjectFieldHeaders = this.subjectFieldHeaders.map(() => {
					return '';
				});
			} else if(this.subjectData.length > 0) {
				this.firstRowHeader = this.subjectData[0];
				this.firstRowHeader.forEach((column, index) => {
					this.$set(this.subjectFieldHeaders, index, this.project.templateFields.filter((field) => {
						return field.effective_label == column || field.label == column || field.field_key == column;
					})[0] ?? null);
				});
			} else {
				this.firstRowHeader = false;
			}
		},
		parseSelectedDataFile(upload) {
			this.dataFileLoading = true;
			this.subjectMatchingError = null;
			this.subjectData = null;
			this.matchedImagesCount = 0;
			this.firstRowHeader = false;
			Papa.parse(upload.file, {
				skipEmptyLines: true,
				encoding: 'ISO-8859-1',
				complete: (results) => {
					this.dataFileLoading = false;
					this.subjectData = results.data;

					// Found list of common ones at https://www.i18nqa.com/debug/utf8-debug.html
					const commonWindows1252Characters = /[\xC2-\xDF][\x80-\xBF]|[\xE0-\xEF][\x80-\xBF]{2}|[\xF0-\xF4][\x80-\xBF]{3}/g;
					this.subjectData.forEach((row) => {
						row.forEach((column, columnIndex) => {
							// Make sure we work with spaces after commas
							row[columnIndex] = column = column.trim();

							let windows1252Matches = column.match(commonWindows1252Characters);
							if(windows1252Matches?.length) {
								console.warn('bad encoding #1: ' + column, windows1252Matches);
								try {
									windows1252Matches.forEach((match) => {
										row[columnIndex] = column = column.replace(match, decodeURIComponent(escape(match)));
									});
								} catch(error) {
									logError(error, 'Failed to decode Win1252 characters', {
										column: column,
										row: row
									});
								}
							}

							[
								{broken: 'â€“', fixed: "—"},
								{broken: "â€”", fixed: "–"},
								{broken: "â€˜", fixed: "‘"},
								{broken: "â€™", fixed: "’"},
								{broken: "â€œ", fixed: "“"},
								// eslint-disable-next-line
								{broken: "â€", fixed: "”"}
							].forEach((replace) => {
								if(column.indexOf(replace.broken) !== -1) {
									console.warn('bad encoding #2: ' + column);
									row[columnIndex] = column = column.replace(replace.broken, replace.fixed);
								}
							});
						});
					});
				},
				error: () => {
					this.dataFileLoading = false;
					this.subjectMatchingError = 'Failed to parse data file';
				}
			});
		},
		lookForReadMe() {
			let readmeFile = this.dataFiles.filter((upload) => {
				return upload.file.name.toLowerCase() === 'readme.txt';
			})[0];
			if(readmeFile) {
				this.dataFileLoading = true;
				let reader = new FileReader();
				reader.readAsText(readmeFile.file, 'UTF-8');
				reader.onload = (event) => {
					let data = event.target.result;
					this.parseReadMe(data.split(/\r?\n/));
				};
				reader.onerror = () => {
					this.dataFileLoading = false;
				};
			}
		},
		parseReadMe(lines) {
			let readmeRowHeader = {};
			lines.forEach((line) => {
				let header = /\[Field Definition #?(\d\d?)\] = (.*)/g.exec(line);
				if(!header || header.length !== 3) {
					return;
				}

				let columnNumber = parseInt(header[1]);
				let column = header[2];
				let index = columnNumber - 1;
				if(isNaN(index) || index >= this.subjectFieldHeaders.length) {
					return;
				}

				Vue.set(this.subjectFieldHeaders, index, this.project.templateFields.filter((field) => {
					return field.effective_label == column || field.label == column || field.field_key == column || 
						((field.effective_label == 'Teacher' || field.field_key == 'Teacher') && column == 'Teacher Name');
				})[0] ?? null);
				readmeRowHeader[index] = column;
			});

			readmeRowHeader = Object.values(readmeRowHeader);
			if(readmeRowHeader.length) {
				this.readmeRowHeader = readmeRowHeader;
			}
			this.dataFileLoading = false;
		},
		lookForCropDataFile() {
			let cropDataFile = this.dataFiles.find((upload) => {
				return upload.file.name.toLowerCase() === 'cropdata.txt';
			});
			this.parseCropDataFile(cropDataFile);
		},
		parseCropDataFile(cropDataFile) {
			if(!cropDataFile) {
				return;
			}

			this.cropDataFileLoading = true;
			Papa.parse(cropDataFile.file, {
				skipEmptyLines: true,
				encoding: 'ISO-8859-1',
				header: true,
				complete: (results) => {
					this.cropDataFileLoading = false;
					if(results.data.length === 0 || !results.data[0]['ImageName']|| !results.data[0]['CropL']) {
						return;
					}

					this.cropData = results.data;
					this.cropDataFile = cropDataFile;
					this.useCropDataFile = true;
				},
				error: () => {
					this.cropDataFileLoading = false;
				}
			});
		},
		updateCropData() {
			if(this.useCropDataFile) {
				this.imageFiles.forEach((imageUpload) => {
					imageUpload.cropData = this.cropData.find((cropRow) => {
						return simpleFilename(cropRow['ImageName']) === simpleFilename(imageUpload.file.name);
					}) ?? null;
				});
			} else {
				this.imageFiles.forEach((imageUpload) => {
					imageUpload.cropData = null;
				});
			}
		},
		getMatchingSubjects(subjects) {
			if(!this.subjectFieldHeaders) {
				return [];
			}

			let mergeFields = this.mergeFields.map(field => field.field_key ?? field.effective_label ?? field.label);
			let propertiesToMatch = this.subjectFieldHeaders.map(field => {
				if(field) {
					return field.field_key ?? field.effective_label ?? field.label;
				} else {
					return null;
				}
			});

			return subjects.map(subjectRow => {
				if(mergeFields.length) {
					return (this.project?.subjects ?? []).find(subject => {
						for(let i = 0; i < mergeFields.length; i++) {
							let prop = mergeFields[i];
							let propertyIndex = propertiesToMatch.indexOf(prop);
							if(prop && propertyIndex !== -1 && subject[prop] != subjectRow[propertyIndex]) {
								return false;
							}
						}

						return true;
					});
				} else {
					// If we don't have any fields mapped, don't try to say everything matches
					if(propertiesToMatch.filter(prop => !!prop).length === 0) {
						return null;
					}

					return (this.project?.subjects ?? []).find(subject => {
						for(let i = 0; i < propertiesToMatch.length; i++) {
							let prop = propertiesToMatch[i];
							if(prop && subject[prop] != subjectRow[i]) {
								return false;
							}
						}

						return true;
					});
				}
			});
		}
	},
	components: {
		SubjectMappingTable,
		RejectedFilesTable
	}
};
</script>

<style scoped>
	.subject-grid-wrapper, .warnings, .merge-settings-wrapper {
		margin-bottom: 1em;
		margin-left: 0.1em;
		margin-right: 0.1em;
		margin-top: 0.1em;
	}
	.merge-settings-wrapper {
		padding: 1em;
		padding-bottom: 0;
	}

	.back {
		margin-right: 1em;
	}

	.warnings {
		width: calc(100% - 0.2em);
	}

	.first-row-header {
		width: fit-content;
	}
	.no-margin-top {
		margin-top: 0;
	}
</style>

<style>
	.v-menu__content .v-list-item {
		text-align: start;
	}
</style>